import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLoad, PageHead, Card, Stat, BarChart, Btn, Badge, StatusBadge, ErrorBox, Skeleton, Empty, Avatar, num, money, ago, fdt } from "../ui";

const short = (d) => {
  const [, m, day] = d.split("-");
  return `${Number(day)}/${Number(m)}`;
};

export default function Overview() {
  const nav = useNavigate();
  const stats = useLoad("stats");
  const att = useLoad("attention");
  const an = useLoad("analytics", { days: 30 });
  const s = stats.data || {};
  const a = att.data;
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const todo = [
    { n: s.pendingPayments, label: "Payment requests", icon: "fa-solid fa-indian-rupee-sign", to: "/website/payments", c: "#f59e0b" },
    { n: s.pendingManagers, label: "Admin Manager applications", icon: "fa-solid fa-user-shield", to: "/website/managers", c: "#10b981" },
    { n: s.monetRequests, label: "Monetization requests", icon: "fa-solid fa-sack-dollar", to: "/website/blogs?monetization=REQUESTED", c: "#8b5cf6" },
    { n: s.newMessages, label: "New contact messages", icon: "fa-regular fa-envelope", to: "/website/messages", c: "#06b6d4" },
    { n: s.scheduled, label: "Scheduled blogs", icon: "fa-regular fa-calendar", to: "/website/blogs?status=SCHEDULED", c: "#3b82f6" },
  ];
  const waiting = todo.filter((t) => t.n > 0 && t.label !== "Scheduled blogs").length;

  return (
    <div>
      <PageHead
        icon="fa-solid fa-globe"
        title="Website Overview"
        subtitle={today}
        actions={
          <>
            <Btn icon="fa-solid fa-rotate" onClick={() => { stats.reload(); att.reload(); an.reload(); }}>Refresh</Btn>
            <Btn variant="primary" icon="fa-solid fa-plus" onClick={() => nav("/website/blogs/new")}>नया Blog</Btn>
          </>
        }
      />
      <ErrorBox msg={stats.error} retry={stats.reload} />

      {(s.maintenance || s.announcement) && (
        <div className="flex flex-wrap gap-2 mb-5">
          {s.maintenance && <Link to="/website/settings?tab=maintenance"><Badge tone="amber" icon="fa-solid fa-screwdriver-wrench" className="!text-xs !py-1 !px-3">Maintenance mode चालू है — लोग साइट नहीं देख पा रहे</Badge></Link>}
          {s.announcement && <Link to="/website/settings?tab=announcement"><Badge tone="cyan" icon="fa-solid fa-bullhorn" className="!text-xs !py-1 !px-3">Announcement bar चालू है</Badge></Link>}
        </div>
      )}

      {/* Needs attention */}
      <Card title="ध्यान दें" subtitle="जिन कामों पर आपका फैसला चाहिए" icon="fa-regular fa-bell" className="mb-5"
        actions={waiting === 0 && !stats.loading ? <Badge tone="green" icon="fa-solid fa-check">सब साफ़</Badge> : null}>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {todo.map((t) => (
            <Link key={t.label} to={t.to} className="flex items-center gap-3 p-3.5 rounded-2xl border wa-line hover:-translate-y-0.5 transition-transform" style={t.n > 0 ? { borderColor: t.c + "66", background: t.c + "0d" } : {}}>
              <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: t.c + "1f", color: t.c }}><i className={t.icon} /></span>
              <span className="flex-1 min-w-0">
                <span className="block text-[12.5px] wa-muted truncate">{t.label}</span>
                <span className="block text-xl font-extrabold tabular-nums">{stats.loading ? "…" : num(t.n)}</span>
              </span>
              {t.n > 0 && <i className="fa-solid fa-chevron-right text-xs wa-muted" />}
            </Link>
          ))}
        </div>
      </Card>

      {/* Key numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5">
        <Stat icon="fa-solid fa-users" label="Total users" value={num(s.users)} hint={`पिछले 7 दिन में +${num(s.newUsers7)} नए`} color="#06b6d4" onClick={() => nav("/website/users")} />
        <Stat icon="fa-solid fa-pen-nib" label="Blogs" value={num(s.blogs)} hint={`${num(s.published)} live · ${num(s.drafts)} draft`} color="#8b5cf6" onClick={() => nav("/website/blogs")} />
        <Stat icon="fa-regular fa-eye" label="Blog views" value={num(s.views)} hint={`Posters: ${num(s.posterViews)} views`} color="#10b981" onClick={() => nav("/website/analytics")} />
        <Stat icon="fa-solid fa-indian-rupee-sign" label="Total revenue" value={money(s.revenue)} hint={`${num(s.activePlans)} plans अभी चालू`} color="#f59e0b" onClick={() => nav("/website/payments?status=APPROVED")} />
        <Stat icon="fa-regular fa-images" label="Posters" value={num(s.posters)} color="#ec4899" onClick={() => nav("/website/posters")} />
        <Stat icon="fa-regular fa-comments" label="Comments" value={num(s.comments)} hint={`${num(s.likes)} likes`} color="#3b82f6" onClick={() => nav("/website/comments")} />
        <Stat icon="fa-solid fa-sack-dollar" label="Monetized blogs" value={num(s.monetApproved)} hint={s.monetRequests ? `${s.monetRequests} request बाकी` : "कोई request बाकी नहीं"} alert={s.monetRequests > 0} color="#a855f7" onClick={() => nav("/website/blogs?monetization=APPROVED")} />
        <Stat icon="fa-solid fa-user-slash" label="Blocked users" value={num(s.blockedUsers)} color="#ef4444" onClick={() => nav("/website/users?filter=blocked")} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
        <Card title="पिछले 30 दिन में blog views" icon="fa-solid fa-chart-column" className="xl:col-span-2"
          actions={<Link to="/website/analytics" className="text-xs font-semibold" style={{ color: "var(--wa-accent)" }}>पूरा Analytics →</Link>}>
          {an.loading ? <Skeleton rows={4} h={40} /> : an.data ? <BarChart label="Daily blog views" data={an.data.days.map((d) => ({ label: d.day, short: short(d.day), value: d.views }))} /> : <ErrorBox msg={an.error} retry={an.reload} />}
        </Card>
        <Card title="नए users (30 दिन)" icon="fa-solid fa-user-plus">
          {an.loading ? <Skeleton rows={4} h={40} /> : an.data ? (
            <>
              <p className="text-3xl font-extrabold tabular-nums">{num(an.data.days.reduce((t, d) => t + d.users, 0))}</p>
              <p className="text-xs wa-muted mb-3">signups</p>
              <BarChart height={140} color="#8b5cf6" label="Daily signups" data={an.data.days.map((d) => ({ label: d.day, short: short(d.day), value: d.users }))} />
            </>
          ) : null}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        <Card title="Pending payments" icon="fa-solid fa-hourglass-half" actions={<Link to="/website/payments" className="text-xs font-semibold" style={{ color: "var(--wa-accent)" }}>सब देखें →</Link>}>
          {!a ? <Skeleton rows={3} /> : a.payments.length === 0 ? <Empty icon="fa-solid fa-check" title="कोई payment बाकी नहीं" /> : (
            <ul className="divide-y wa-line">
              {a.payments.map((p) => (
                <li key={p.id} className="py-2.5 flex items-center gap-3">
                  <Avatar name={p.name} size={32} />
                  <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{p.name}</p><p className="text-[11.5px] wa-muted">{p.plan} · {ago(p.at)}</p></div>
                  <b className="tabular-nums text-sm">{money(p.amount)}</b>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="नए users" icon="fa-solid fa-user-plus" actions={<Link to="/website/users" className="text-xs font-semibold" style={{ color: "var(--wa-accent)" }}>सब देखें →</Link>}>
          {!a ? <Skeleton rows={3} /> : a.recentUsers.length === 0 ? <Empty title="अभी कोई user नहीं" /> : (
            <ul className="divide-y wa-line">
              {a.recentUsers.map((u) => (
                <li key={u.id}>
                  <Link to={`/website/users?open=${u.id}`} className="py-2.5 flex items-center gap-3">
                    <Avatar name={u.name || u.email} image={u.image} size={32} />
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{u.name || "—"}</p><p className="text-[11.5px] wa-muted truncate">{u.email}</p></div>
                    <span className="text-[11px] wa-muted whitespace-nowrap">{ago(u.createdAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="हाल में बदले blogs" icon="fa-solid fa-pen-nib" actions={<Link to="/website/blogs" className="text-xs font-semibold" style={{ color: "var(--wa-accent)" }}>सब देखें →</Link>}>
          {!a ? <Skeleton rows={3} /> : a.recentBlogs.length === 0 ? <Empty title="अभी कोई blog नहीं" /> : (
            <ul className="divide-y wa-line">
              {a.recentBlogs.map((b) => (
                <li key={b.id}>
                  <Link to={`/website/blogs/${b.id}`} className="py-2.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{b.title}</p><p className="text-[11.5px] wa-muted truncate">{b.author} · {ago(b.at)}</p></div>
                    <StatusBadge s={b.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
        {a && a.messages.length > 0 && (
          <Card title="नए contact messages" icon="fa-regular fa-envelope" className="lg:col-span-2 xl:col-span-3" actions={<Link to="/website/messages" className="text-xs font-semibold" style={{ color: "var(--wa-accent)" }}>Inbox खोलें →</Link>}>
            <ul className="divide-y wa-line">
              {a.messages.map((m) => (
                <li key={m.id} className="py-2.5 flex items-start gap-3">
                  <Avatar name={m.name} size={32} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm"><b>{m.name}</b> <span className="wa-muted text-xs">· {m.email} · {fdt(m.createdAt)}</span></p>
                    <p className="text-sm wa-muted truncate">{m.subject ? m.subject + " — " : ""}{m.message}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
